import {
  validateAppointmentBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import { createBookingDraftId } from "@/app/booking/core/draftId";
import {
  addAppointmentLocalDateDays,
  isAppointmentLocalDate,
} from "@/app/booking/scheduling/appointmentAvailabilityProvider";
import { buildAppointmentSchedulePlan } from "@/app/booking/scheduling/appointmentSchedulePlan";
import {
  appointmentTimeProfessionalMutationEvidenceStructurallyMatchesDraft,
  haveExactAppointmentTimeProfessionalMutationAuthority,
  isAppointmentTimeProfessionalMutationEvidenceCurrent,
  validateAppointmentTimeProfessionalMutationEvidence,
  type AppointmentTimeProfessionalMutationEvidenceV1,
  type AppointmentTimeProfessionalMutationProviderIdentity,
} from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";
import {
  appointmentDraftRequiresStaffChoiceEvidence,
  appointmentStaffChoiceEvidenceMatchesDraft,
  validateAppointmentStaffChoiceEvidence,
  type AppointmentStaffChoiceEvidenceV2,
} from "@/app/booking/scheduling/appointmentStaffChoiceEvidence";
import {
  validateAppointmentProfessionalSelectionEvidence,
  type AppointmentProfessionalSelectionEvidenceV1,
} from "@/app/booking/scheduling/appointmentProfessionalSelection";

import {
  appointmentWaitlistConfigurationCoversPlan,
  haveExactAppointmentWaitlistConfigurationAuthority,
  isAppointmentWaitlistConfigurationPublicationCurrent,
  isAppointmentWaitlistConflictPublicationCurrent,
  resolveAppointmentWaitlistTimeOptionsForDate,
  validateAppointmentWaitlistConfigurationPublication,
  validateAppointmentWaitlistConflictPublication,
  validateAppointmentWaitlistPreferencesForConfiguration,
  retiredGroupAppointmentWaitlistConfigurationCoversPlanForStorageMigration,
  validateRetiredGroupAppointmentWaitlistConfigurationPublicationForStorageMigration,
  validateRetiredGroupAppointmentWaitlistPreferencesForConfigurationForStorageMigration,
  type AppointmentWaitlistConfigurationPublicationV1,
  type AppointmentWaitlistConflictPublicationV1,
  type AppointmentWaitlistPreferences,
  type AppointmentWaitlistProviderIdentity,
} from "./appointmentWaitlistConfiguration";
import {
  appointmentWaitlistPreSlotQuoteSnapshotMatches,
  resolveAppointmentWaitlistPreSlotQuoteSnapshot,
  type AppointmentWaitlistPreSlotQuoteSnapshotV1,
} from "./appointmentWaitlistPreSlotQuote";

/** V3 is the dedicated staged Waitlist journey. V1/V2 never coerce. */
export const APPOINTMENT_WAITLIST_DRAFT_SCHEMA_VERSION = 3 as const;
export const APPOINTMENT_WAITLIST_NOTE_MAX_LENGTH = 500;
export const APPOINTMENT_WAITLIST_MAX_DATE_TIME_PREFERENCES = 6;
export const APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES = 15;

export type { AppointmentWaitlistPreferences } from "./appointmentWaitlistConfiguration";

export type AppointmentWaitlistStage =
  "preferences" | "first-visit" | "confirmation";
export type AppointmentWaitlistFirstVisitAnswer = "yes" | "no";

export interface AppointmentWaitlistConfirmation {
  kind: "confirmation";
  note: string | null;
  paymentPresentation: AppointmentWaitlistConfigurationPublicationV1["paymentPresentation"];
}

export type AppointmentWaitlistTerminalFailureReason =
  | "invalid-customer"
  | "invalid-draft"
  | "provider-response-invalid"
  | "stale-draft";
export interface AppointmentWaitlistTerminalFailure {
  reason: AppointmentWaitlistTerminalFailureReason;
  idempotencyKey: string;
  requestFingerprint: string;
}

export interface AppointmentWaitlistDraftV3 {
  schemaVersion: typeof APPOINTMENT_WAITLIST_DRAFT_SCHEMA_VERSION;
  draftId: string;
  kind: "appointment-waitlist";
  sourceAppointmentDraftSchemaVersion: 1;
  customerAccountId: string | null;
  appointmentDraft: AppointmentBookingDraftV1;
  staffChoiceEvidence: AppointmentStaffChoiceEvidenceV2 | null;
  timeProfessionalMutationEvidence: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  professionalSelectionEvidence: AppointmentProfessionalSelectionEvidenceV1 | null;
  preSlotQuoteSnapshot: AppointmentWaitlistPreSlotQuoteSnapshotV1;
  /** UX-only return anchor. Config owns the independent Waitlist horizon. */
  returnDateId: string;
  configurationPublication: AppointmentWaitlistConfigurationPublicationV1;
  conflictPublication: AppointmentWaitlistConflictPublicationV1 | null;
  preferences: AppointmentWaitlistPreferences;
  stage: AppointmentWaitlistStage;
  firstVisitAnswer: AppointmentWaitlistFirstVisitAnswer | null;
  confirmation: AppointmentWaitlistConfirmation | null;
  terminalFailure: AppointmentWaitlistTerminalFailure | null;
}

export type AppointmentWaitlistDraftValidationResult =
  | { valid: true; draft: AppointmentWaitlistDraftV3 }
  | { valid: false; reason: string };

export interface AppointmentWaitlistActionAuthority {
  currentConfigurationPublication: AppointmentWaitlistConfigurationPublicationV1;
  currentStaffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  currentTimeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  currentProfessionalSelectionEvidence?: AppointmentProfessionalSelectionEvidenceV1 | null;
  evaluatedAtEpochMs?: number;
  expectedConfigurationProviderIdentity: AppointmentWaitlistProviderIdentity;
  expectedTimeProfessionalMutationProviderIdentity?: AppointmentTimeProfessionalMutationProviderIdentity;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

function same(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function hash(value: string, seed: number) {
  let result = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    result = Math.imul(result ^ value.charCodeAt(index), 16_777_619);
  }
  return (result >>> 0).toString(16).padStart(8, "0");
}

function expectedIdempotencyKey(draft: AppointmentWaitlistDraftV3) {
  const request = {
    schemaVersion: draft.schemaVersion,
    kind: draft.kind,
    draftId: draft.draftId,
    sourceAppointmentDraftSchemaVersion:
      draft.sourceAppointmentDraftSchemaVersion,
    customerAccountId: draft.customerAccountId,
    appointmentDraft: draft.appointmentDraft,
    staffChoiceEvidence: draft.staffChoiceEvidence,
    professionalSelectionEvidence: draft.professionalSelectionEvidence,
    preSlotQuoteSnapshot: draft.preSlotQuoteSnapshot,
    timeProfessionalMutationEvidence: draft.timeProfessionalMutationEvidence
      ? (() => {
          const {
            generatedAtEpochMs: omittedGeneratedAt,
            expiresAtEpochMs: omittedExpiresAt,
            ...staticEvidence
          } = draft.timeProfessionalMutationEvidence;
          void omittedGeneratedAt;
          void omittedExpiresAt;
          return staticEvidence;
        })()
      : null,
    configurationAuthority: (() => {
      const {
        publicationId: omittedPublicationId,
        generatedAtEpochMs: omittedGeneratedAt,
        expiresAtEpochMs: omittedExpiresAt,
        ...staticConfiguration
      } = draft.configurationPublication;
      void omittedPublicationId;
      void omittedGeneratedAt;
      void omittedExpiresAt;
      return staticConfiguration;
    })(),
    preferences: draft.preferences,
    firstVisitAnswer: draft.firstVisitAnswer,
    confirmation: draft.confirmation,
  };
  const serialized = JSON.stringify(request);
  return `appointment-waitlist-v3-${[
    0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35,
  ]
    .map((seed) => hash(serialized, seed))
    .join("")}`;
}

function expectedTerminalRequestFingerprint(
  draft: AppointmentWaitlistDraftV3,
) {
  const serialized = JSON.stringify({
    preferences: draft.preferences,
    firstVisitAnswer: draft.firstVisitAnswer,
    confirmation: draft.confirmation,
  });
  return `appointment-waitlist-terminal-v1-${[
    0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35,
  ]
    .map((seed) => hash(serialized, seed))
    .join("")}`;
}

function cloneConfirmation(
  value: unknown,
  configuration: AppointmentWaitlistConfigurationPublicationV1,
) {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    value.kind !== "confirmation" ||
    !same(value.paymentPresentation, configuration.paymentPresentation)
  ) {
    return undefined;
  }
  const note =
    value.note === null
      ? null
      : typeof value.note === "string" &&
          value.note === value.note.trim() &&
          value.note.length > 0 &&
          value.note.length <= APPOINTMENT_WAITLIST_NOTE_MAX_LENGTH
        ? value.note
        : undefined;
  return note === undefined
    ? undefined
    : ({
        kind: "confirmation",
        note,
        paymentPresentation: configuration.paymentPresentation,
      } satisfies AppointmentWaitlistConfirmation);
}

function cloneTerminalFailure(
  value: unknown,
  draft: AppointmentWaitlistDraftV3,
) {
  if (
    !isRecord(value) ||
    draft.customerAccountId === null ||
    (value.reason !== "invalid-customer" &&
      value.reason !== "invalid-draft" &&
      value.reason !== "provider-response-invalid" &&
      value.reason !== "stale-draft") ||
    !isIdentifier(value.idempotencyKey) ||
    !isIdentifier(value.requestFingerprint)
  ) {
    return null;
  }
  return {
    reason: value.reason,
    idempotencyKey: value.idempotencyKey,
    requestFingerprint: value.requestFingerprint,
  } satisfies AppointmentWaitlistTerminalFailure;
}

/** Structural/store validation only; expiry and runtime drift are action gates. */
function validateAppointmentWaitlistDraftForMode(
  value: unknown,
  expectedMode: AppointmentBookingDraftV1["mode"],
): AppointmentWaitlistDraftValidationResult {
  if (
    !isRecord(value) ||
    value.schemaVersion !== APPOINTMENT_WAITLIST_DRAFT_SCHEMA_VERSION ||
    value.kind !== "appointment-waitlist" ||
    value.sourceAppointmentDraftSchemaVersion !== 1 ||
    !isIdentifier(value.draftId) ||
    !Object.prototype.hasOwnProperty.call(
      value,
      "timeProfessionalMutationEvidence",
    ) ||
    !Object.prototype.hasOwnProperty.call(
      value,
      "professionalSelectionEvidence",
    ) ||
    !Object.prototype.hasOwnProperty.call(value, "preSlotQuoteSnapshot") ||
    !Object.prototype.hasOwnProperty.call(value, "terminalFailure") ||
    (value.customerAccountId !== null &&
      !isIdentifier(value.customerAccountId)) ||
    (value.stage !== "preferences" &&
      value.stage !== "first-visit" &&
      value.stage !== "confirmation")
  ) {
    return { valid: false, reason: "invalid-shape" };
  }

  const appointment = validateAppointmentBookingDraft(value.appointmentDraft);
  if (!appointment.valid || appointment.draft.mode !== expectedMode) {
    return { valid: false, reason: "invalid-appointment-draft" };
  }
  const planResult = buildAppointmentSchedulePlan(appointment.draft);
  if (!planResult.ok) {
    return { valid: false, reason: "invalid-schedule-plan" };
  }
  const configuration =
    expectedMode === "individual"
      ? validateAppointmentWaitlistConfigurationPublication(
          value.configurationPublication,
        )
      : validateRetiredGroupAppointmentWaitlistConfigurationPublicationForStorageMigration(
          value.configurationPublication,
        );
  const configurationCoversPlan = configuration
    ? expectedMode === "individual"
      ? appointmentWaitlistConfigurationCoversPlan({
          publication: configuration,
          plan: planResult.plan,
        })
      : retiredGroupAppointmentWaitlistConfigurationCoversPlanForStorageMigration(
          {
            publication: configuration,
            plan: planResult.plan,
          },
        )
    : false;
  if (
    !configuration ||
    configuration.waitlistDraftId !== value.draftId ||
    configuration.sourceAppointmentDraftId !== appointment.draft.draftId ||
    !configurationCoversPlan ||
    !isAppointmentLocalDate(value.returnDateId) ||
    value.returnDateId < configuration.bookingWindow.earliestDate ||
    value.returnDateId > configuration.bookingWindow.latestDate
  ) {
    return { valid: false, reason: "invalid-waitlist-configuration" };
  }

  const rawTimeEvidence = value.timeProfessionalMutationEvidence;
  const timeEvidence =
    rawTimeEvidence === null
      ? null
      : validateAppointmentTimeProfessionalMutationEvidence(rawTimeEvidence);
  const specialistId =
    planResult.plan.origin.kind === "specialist"
      ? planResult.plan.origin.userLocationId
      : null;
  const specialistWasMutated =
    specialistId !== null &&
    planResult.plan.tasks.some(
      ({ staffPreference }) =>
        staffPreference.kind !== "specific" ||
        staffPreference.source !== "specialist-entry" ||
        staffPreference.userLocationId !== specialistId,
    );
  if (
    (rawTimeEvidence !== null &&
      (!timeEvidence?.valid ||
        !appointmentTimeProfessionalMutationEvidenceStructurallyMatchesDraft({
          draft: appointment.draft,
          evidence: timeEvidence.evidence,
          locationEligibilityPublication:
            timeEvidence.evidence.locationEligibilityPublication,
        }))) ||
    (specialistWasMutated && !timeEvidence?.valid)
  ) {
    return { valid: false, reason: "invalid-time-professional-evidence" };
  }

  const staffEvidence =
    value.staffChoiceEvidence === null
      ? null
      : validateAppointmentStaffChoiceEvidence(value.staffChoiceEvidence);
  const hasTimeEvidence = timeEvidence?.valid === true;
  const staffIsValid = appointmentDraftRequiresStaffChoiceEvidence(
    appointment.draft,
  )
    ? Boolean(
        staffEvidence?.valid &&
        appointmentStaffChoiceEvidenceMatchesDraft({
          draft: appointment.draft,
          evidence: staffEvidence.evidence,
        }),
      )
    : value.staffChoiceEvidence === null;
  if (hasTimeEvidence ? value.staffChoiceEvidence !== null : !staffIsValid) {
    return { valid: false, reason: "invalid-staff-choice-evidence" };
  }

  const rawProfessionalSelectionEvidence = value.professionalSelectionEvidence;
  const professionalSelectionEvidence =
    rawProfessionalSelectionEvidence === null
      ? null
      : validateAppointmentProfessionalSelectionEvidence(
          rawProfessionalSelectionEvidence,
        );
  const expectsProfessionalSelectionEvidence = Boolean(
    !hasTimeEvidence && staffEvidence?.valid && staffEvidence.evidence.professionalSelection,
  );
  if (
    (rawProfessionalSelectionEvidence !== null &&
      !professionalSelectionEvidence?.valid) ||
    expectsProfessionalSelectionEvidence !==
      Boolean(professionalSelectionEvidence?.valid)
  ) {
    return { valid: false, reason: "invalid-professional-selection-evidence" };
  }
  const preSlotQuoteSnapshot = appointmentWaitlistPreSlotQuoteSnapshotMatches({
    appointmentDraft: appointment.draft,
    snapshot: value.preSlotQuoteSnapshot,
    staffChoiceEvidence: staffEvidence?.valid ? staffEvidence.evidence : null,
    timeProfessionalMutationEvidence: hasTimeEvidence
      ? timeEvidence.evidence
      : null,
    professionalSelectionEvidence: professionalSelectionEvidence?.valid
      ? professionalSelectionEvidence.evidence
      : null,
  });
  if (!preSlotQuoteSnapshot) {
    return { valid: false, reason: "invalid-pre-slot-quote" };
  }

  const preferences = isRecord(value.preferences)
    ? expectedMode === "individual"
      ? validateAppointmentWaitlistPreferencesForConfiguration({
          publication: configuration,
          preferences:
            value.preferences as unknown as AppointmentWaitlistPreferences,
          maximumPreferences: APPOINTMENT_WAITLIST_MAX_DATE_TIME_PREFERENCES,
        })
      : validateRetiredGroupAppointmentWaitlistPreferencesForConfigurationForStorageMigration(
          {
            publication: configuration,
            preferences:
              value.preferences as unknown as AppointmentWaitlistPreferences,
            maximumPreferences: APPOINTMENT_WAITLIST_MAX_DATE_TIME_PREFERENCES,
          },
        )
    : null;
  if (!preferences) return { valid: false, reason: "invalid-preferences" };
  const conflict =
    value.conflictPublication === null
      ? null
      : validateAppointmentWaitlistConflictPublication({
          value: value.conflictPublication,
          configuration,
          preferences,
        });
  if (value.conflictPublication !== null && !conflict) {
    return { valid: false, reason: "invalid-conflict-publication" };
  }
  const answer =
    value.firstVisitAnswer === "yes" || value.firstVisitAnswer === "no"
      ? value.firstVisitAnswer
      : value.firstVisitAnswer === null
        ? null
        : undefined;
  const confirmation = cloneConfirmation(value.confirmation, configuration);
  if (answer === undefined || confirmation === undefined) {
    return { valid: false, reason: "invalid-descendants" };
  }
  const requiresFirstVisit = configuration.firstVisit.kind === "required";
  const stageIsValid =
    value.stage === "preferences"
      ? answer === null && confirmation === null
      : value.customerAccountId !== null &&
        conflict?.result.kind === "none" &&
        (value.stage === "first-visit"
          ? requiresFirstVisit && confirmation === null
          : confirmation !== null &&
            (requiresFirstVisit ? answer !== null : answer === null));
  if (!stageIsValid) return { valid: false, reason: "invalid-stage" };

  const active: AppointmentWaitlistDraftV3 = {
    schemaVersion: APPOINTMENT_WAITLIST_DRAFT_SCHEMA_VERSION,
    draftId: value.draftId,
    kind: "appointment-waitlist",
    sourceAppointmentDraftSchemaVersion: 1,
    customerAccountId: value.customerAccountId,
    appointmentDraft: appointment.draft,
    staffChoiceEvidence: hasTimeEvidence
      ? null
      : staffEvidence?.valid
        ? staffEvidence.evidence
        : null,
    timeProfessionalMutationEvidence: hasTimeEvidence
      ? timeEvidence.evidence
      : null,
    professionalSelectionEvidence: professionalSelectionEvidence?.valid
      ? professionalSelectionEvidence.evidence
      : null,
    preSlotQuoteSnapshot,
    returnDateId: value.returnDateId,
    configurationPublication: configuration,
    conflictPublication: conflict,
    preferences,
    stage: value.stage,
    firstVisitAnswer: answer,
    confirmation,
    terminalFailure: null,
  };
  const terminal =
    value.terminalFailure === null
      ? null
      : cloneTerminalFailure(value.terminalFailure, active);
  if (value.terminalFailure !== null && !terminal) {
    return { valid: false, reason: "invalid-terminal-failure" };
  }
  return { valid: true, draft: { ...active, terminalFailure: terminal } };
}

export function validateAppointmentWaitlistDraft(
  value: unknown,
): AppointmentWaitlistDraftValidationResult {
  return validateAppointmentWaitlistDraftForMode(value, "individual");
}

/** Canonical historical validator used only by the one-way storage retiree. */
export function validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(
  value: unknown,
): AppointmentWaitlistDraftValidationResult {
  return validateAppointmentWaitlistDraftForMode(value, "group");
}

export function validateAppointmentWaitlistDraftForAction(
  value: unknown,
  authority: AppointmentWaitlistActionAuthority,
) {
  const draft = validateAppointmentWaitlistDraft(value);
  const evaluatedAtEpochMs = authority.evaluatedAtEpochMs ?? Date.now();
  if (
    !draft.valid ||
    !haveExactAppointmentWaitlistConfigurationAuthority(
      draft.draft.configurationPublication,
      authority.currentConfigurationPublication,
    ) ||
    !isAppointmentWaitlistConfigurationPublicationCurrent({
      publication: authority.currentConfigurationPublication,
      expectedProviderIdentity: authority.expectedConfigurationProviderIdentity,
      evaluatedAtEpochMs,
    })
  ) {
    return { valid: false as const, reason: "stale-configuration" };
  }
  const storedTime = draft.draft.timeProfessionalMutationEvidence;
  const storedStaff = draft.draft.staffChoiceEvidence;
  if (storedStaff) {
    const currentStaff = authority.currentStaffChoiceEvidence;
    if (
      !currentStaff ||
      !validateAppointmentStaffChoiceEvidence(currentStaff).valid ||
      !appointmentStaffChoiceEvidenceMatchesDraft({
        draft: draft.draft.appointmentDraft,
        evidence: currentStaff,
      }) ||
      !same(storedStaff, currentStaff)
    ) {
      return { valid: false as const, reason: "stale-staff-authority" };
    }
  } else if (authority.currentStaffChoiceEvidence) {
    return { valid: false as const, reason: "unexpected-staff-authority" };
  }
  const storedProfessionalSelection =
    draft.draft.professionalSelectionEvidence;
  const currentProfessionalSelection =
    authority.currentProfessionalSelectionEvidence ?? null;
  if (
    Boolean(storedProfessionalSelection) !== Boolean(currentProfessionalSelection) ||
    (storedProfessionalSelection &&
      !same(storedProfessionalSelection, currentProfessionalSelection))
  ) {
    return { valid: false as const, reason: "stale-professional-selection-authority" };
  }
  if (storedTime) {
    const currentTime = authority.currentTimeProfessionalMutationEvidence;
    const expectedTime =
      authority.expectedTimeProfessionalMutationProviderIdentity;
    if (
      !currentTime ||
      !expectedTime ||
      !haveExactAppointmentTimeProfessionalMutationAuthority(
        storedTime,
        currentTime,
      ) ||
      !isAppointmentTimeProfessionalMutationEvidenceCurrent({
        evidence: currentTime,
        expectedProviderIdentity: expectedTime,
        evaluatedAtEpochMs,
      })
    ) {
      return { valid: false as const, reason: "stale-time-authority" };
    }
  }
  const currentSnapshot = resolveAppointmentWaitlistPreSlotQuoteSnapshot({
    appointmentDraft: draft.draft.appointmentDraft,
    staffChoiceEvidence: authority.currentStaffChoiceEvidence ?? null,
    timeProfessionalMutationEvidence:
      authority.currentTimeProfessionalMutationEvidence ?? null,
    professionalSelectionEvidence: currentProfessionalSelection,
  });
  if (!currentSnapshot || !same(currentSnapshot, draft.draft.preSlotQuoteSnapshot)) {
    return { valid: false as const, reason: "stale-pre-slot-quote" };
  }
  return { valid: true as const, draft: draft.draft };
}

export function createAppointmentWaitlistDraftIdempotencyKey(value: unknown) {
  const draft = validateAppointmentWaitlistDraft(value);
  return draft.valid &&
    draft.draft.stage === "confirmation" &&
    draft.draft.customerAccountId !== null
    ? expectedIdempotencyKey(draft.draft)
    : null;
}

export function createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration(
  value: unknown,
) {
  const draft =
    validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(value);
  return draft.valid &&
    draft.draft.stage === "confirmation" &&
    draft.draft.customerAccountId !== null
    ? expectedIdempotencyKey(draft.draft)
    : null;
}

export function isAppointmentWaitlistDraftTerminallyBlocked(value: unknown) {
  const draft = validateAppointmentWaitlistDraft(value);
  return Boolean(
    draft.valid &&
      draft.draft.terminalFailure &&
      draft.draft.terminalFailure.requestFingerprint ===
        expectedTerminalRequestFingerprint(draft.draft),
  );
}

export function createAppointmentWaitlistDraft(input: {
  appointmentDraft: AppointmentBookingDraftV1;
  configurationPublication: AppointmentWaitlistConfigurationPublicationV1;
  returnDateId: string;
  staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  professionalSelectionEvidence?: AppointmentProfessionalSelectionEvidenceV1 | null;
  draftId?: string;
}) {
  if (input.appointmentDraft.mode !== "individual") return null;
  const draftId = input.draftId ?? createBookingDraftId("appointment-waitlist");
  if (input.configurationPublication.waitlistDraftId !== draftId) return null;
  let defaultDate = input.returnDateId;
  let options = resolveAppointmentWaitlistTimeOptionsForDate(
    input.configurationPublication,
    defaultDate,
  );
  if (
    !options ||
    (!options.allowsAny &&
      options.dayparts.length === 0 &&
      options.intervals.length === 0)
  ) {
    options = null;
    const dates: string[] = [];
    for (let offset = 0; offset < 366; offset += 1) {
      const date = addAppointmentLocalDateDays(input.returnDateId, offset);
      if (!date || date > input.configurationPublication.bookingWindow.latestDate)
        break;
      dates.push(date);
    }
    for (let offset = 0; offset < 366; offset += 1) {
      const date = addAppointmentLocalDateDays(
        input.configurationPublication.bookingWindow.earliestDate,
        offset,
      );
      if (!date || date >= input.returnDateId) break;
      dates.push(date);
    }
    for (const date of dates) {
      const candidate = resolveAppointmentWaitlistTimeOptionsForDate(
        input.configurationPublication,
        date,
      );
      if (
        candidate &&
        (candidate.allowsAny ||
          candidate.dayparts.length > 0 ||
          candidate.intervals.length > 0)
      ) {
        defaultDate = date;
        options = candidate;
        break;
      }
    }
  }
  const time = options?.allowsAny
    ? ({ kind: "any" } as const)
    : options?.dayparts[0]
      ? ({ kind: "daypart", daypart: options.dayparts[0].daypart } as const)
      : options?.intervals[0]
        ? ({ kind: "window", ...options.intervals[0] } as const)
        : null;
  if (!time) return null;
  const preSlotQuoteSnapshot = resolveAppointmentWaitlistPreSlotQuoteSnapshot({
    appointmentDraft: input.appointmentDraft,
    staffChoiceEvidence: input.staffChoiceEvidence ?? null,
    timeProfessionalMutationEvidence:
      input.timeProfessionalMutationEvidence ?? null,
    professionalSelectionEvidence:
      input.professionalSelectionEvidence ?? null,
  });
  if (!preSlotQuoteSnapshot) return null;
  const draft = validateAppointmentWaitlistDraft({
    schemaVersion: APPOINTMENT_WAITLIST_DRAFT_SCHEMA_VERSION,
    draftId,
    kind: "appointment-waitlist",
    sourceAppointmentDraftSchemaVersion: 1,
    customerAccountId: null,
    appointmentDraft: input.appointmentDraft,
    staffChoiceEvidence: input.staffChoiceEvidence ?? null,
    timeProfessionalMutationEvidence:
      input.timeProfessionalMutationEvidence ?? null,
    professionalSelectionEvidence:
      input.professionalSelectionEvidence ?? null,
    preSlotQuoteSnapshot,
    returnDateId: input.returnDateId,
    configurationPublication: input.configurationPublication,
    conflictPublication: null,
    preferences: { dateTimePreferences: [{ date: defaultDate, time }] },
    stage: "preferences",
    firstVisitAnswer: null,
    confirmation: null,
    terminalFailure: null,
  });
  return draft.valid ? draft.draft : null;
}

export function bindAppointmentWaitlistCustomer(input: {
  draft: AppointmentWaitlistDraftV3;
  customerAccountId: string;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (
    !current.valid ||
    !isIdentifier(input.customerAccountId) ||
    (current.draft.customerAccountId !== null &&
      current.draft.customerAccountId !== input.customerAccountId)
  )
    return null;
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    customerAccountId: input.customerAccountId,
  });
  return next.valid ? next.draft : null;
}

export function setAppointmentWaitlistPreferences(input: {
  draft: AppointmentWaitlistDraftV3;
  preferences: AppointmentWaitlistPreferences;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (!current.valid || current.draft.stage !== "preferences") return null;
  const changed = !same(current.draft.preferences, input.preferences);
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    preferences: input.preferences,
    conflictPublication: changed ? null : current.draft.conflictPublication,
    firstVisitAnswer: null,
    confirmation: null,
    terminalFailure: current.draft.terminalFailure,
  });
  return next.valid ? next.draft : null;
}

export function setAppointmentWaitlistConflictPublication(input: {
  draft: AppointmentWaitlistDraftV3;
  conflictPublication: AppointmentWaitlistConflictPublicationV1;
  expectedConfigurationProviderIdentity: AppointmentWaitlistProviderIdentity;
  evaluatedAtEpochMs?: number;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (
    !current.valid ||
    current.draft.stage !== "preferences" ||
    !isAppointmentWaitlistConflictPublicationCurrent({
      publication: input.conflictPublication,
      configuration: current.draft.configurationPublication,
      preferences: current.draft.preferences,
      expectedProviderIdentity: input.expectedConfigurationProviderIdentity,
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
    })
  )
    return null;
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    conflictPublication: input.conflictPublication,
  });
  return next.valid ? next.draft : null;
}

export function refreshAppointmentWaitlistAuthority(input: {
  draft: AppointmentWaitlistDraftV3;
  configurationPublication: AppointmentWaitlistConfigurationPublicationV1;
  conflictPublication: AppointmentWaitlistConflictPublicationV1;
  actionAuthority: AppointmentWaitlistActionAuthority;
}) {
  const current = validateAppointmentWaitlistDraftForAction(input.draft, {
    ...input.actionAuthority,
    currentConfigurationPublication: input.configurationPublication,
  });
  if (
    !current.valid ||
    !isAppointmentWaitlistConflictPublicationCurrent({
      publication: input.conflictPublication,
      configuration: input.configurationPublication,
      preferences: current.draft.preferences,
      expectedProviderIdentity:
        input.actionAuthority.expectedConfigurationProviderIdentity,
      evaluatedAtEpochMs: input.actionAuthority.evaluatedAtEpochMs,
    })
  )
    return null;
  const bookable = input.conflictPublication.result.kind === "bookable";
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    configurationPublication: input.configurationPublication,
    conflictPublication: input.conflictPublication,
    timeProfessionalMutationEvidence:
      input.actionAuthority.currentTimeProfessionalMutationEvidence ?? null,
    professionalSelectionEvidence:
      input.actionAuthority.currentProfessionalSelectionEvidence ?? null,
    stage: bookable ? "preferences" : current.draft.stage,
    firstVisitAnswer: bookable ? null : current.draft.firstVisitAnswer,
    confirmation: bookable ? null : current.draft.confirmation,
    terminalFailure: current.draft.terminalFailure,
  });
  return next.valid ? next.draft : null;
}

export function advanceAppointmentWaitlistFromPreferences(input: {
  draft: AppointmentWaitlistDraftV3;
  actionAuthority: AppointmentWaitlistActionAuthority;
}) {
  const current = validateAppointmentWaitlistDraftForAction(
    input.draft,
    input.actionAuthority,
  );
  if (
    !current.valid ||
    current.draft.stage !== "preferences" ||
    current.draft.customerAccountId === null ||
    !current.draft.conflictPublication ||
    current.draft.conflictPublication.result.kind !== "none" ||
    !isAppointmentWaitlistConflictPublicationCurrent({
      publication: current.draft.conflictPublication,
      configuration: current.draft.configurationPublication,
      preferences: current.draft.preferences,
      expectedProviderIdentity:
        input.actionAuthority.expectedConfigurationProviderIdentity,
      evaluatedAtEpochMs: input.actionAuthority.evaluatedAtEpochMs,
    })
  )
    return null;
  const requiresQuestion =
    current.draft.configurationPublication.firstVisit.kind === "required";
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    stage: requiresQuestion ? "first-visit" : "confirmation",
    confirmation: requiresQuestion
      ? null
      : {
          kind: "confirmation",
          note: null,
          paymentPresentation:
            current.draft.configurationPublication.paymentPresentation,
        },
  });
  return next.valid ? next.draft : null;
}

export function setAppointmentWaitlistFirstVisitAnswer(input: {
  draft: AppointmentWaitlistDraftV3;
  answer: AppointmentWaitlistFirstVisitAnswer;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (!current.valid || current.draft.stage !== "first-visit") return null;
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    firstVisitAnswer: input.answer,
  });
  return next.valid ? next.draft : null;
}

export function advanceAppointmentWaitlistFromFirstVisit(input: {
  draft: AppointmentWaitlistDraftV3;
  actionAuthority: AppointmentWaitlistActionAuthority;
}) {
  const current = validateAppointmentWaitlistDraftForAction(
    input.draft,
    input.actionAuthority,
  );
  if (
    !current.valid ||
    current.draft.stage !== "first-visit" ||
    current.draft.firstVisitAnswer === null
  )
    return null;
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    stage: "confirmation",
    confirmation: {
      kind: "confirmation",
      note: null,
      paymentPresentation:
        current.draft.configurationPublication.paymentPresentation,
    },
  });
  return next.valid ? next.draft : null;
}

export function setAppointmentWaitlistConfirmationNote(input: {
  draft: AppointmentWaitlistDraftV3;
  note: string;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (
    !current.valid ||
    current.draft.stage !== "confirmation" ||
    !current.draft.confirmation
  )
    return null;
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    confirmation: {
      ...current.draft.confirmation,
      note: input.note.trim() || null,
    },
    terminalFailure: current.draft.terminalFailure,
  });
  return next.valid ? next.draft : null;
}

export function retreatAppointmentWaitlistStage(input: {
  draft: AppointmentWaitlistDraftV3;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (!current.valid || current.draft.stage === "preferences") return null;
  const backToQuestion =
    current.draft.stage === "confirmation" &&
    current.draft.configurationPublication.firstVisit.kind === "required";
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    stage: backToQuestion ? "first-visit" : "preferences",
    firstVisitAnswer: backToQuestion ? current.draft.firstVisitAnswer : null,
    confirmation: null,
    terminalFailure: current.draft.terminalFailure,
  });
  return next.valid ? next.draft : null;
}

export function markAppointmentWaitlistDraftTerminalFailure(input: {
  draft: AppointmentWaitlistDraftV3;
  reason: AppointmentWaitlistTerminalFailureReason;
  idempotencyKey: string;
}) {
  const current = validateAppointmentWaitlistDraft(input.draft);
  if (
    !current.valid ||
    current.draft.stage !== "confirmation" ||
    current.draft.customerAccountId === null
  )
    return null;
  if (current.draft.terminalFailure) {
    if (
      current.draft.terminalFailure.requestFingerprint ===
      expectedTerminalRequestFingerprint(current.draft)
    ) {
      return same(current.draft.terminalFailure, {
        reason: input.reason,
        idempotencyKey: input.idempotencyKey,
        requestFingerprint: expectedTerminalRequestFingerprint(current.draft),
      })
        ? current.draft
        : null;
    }
  }
  const next = validateAppointmentWaitlistDraft({
    ...current.draft,
    terminalFailure: {
      reason: input.reason,
      idempotencyKey: input.idempotencyKey,
      requestFingerprint: expectedTerminalRequestFingerprint(current.draft),
    },
  });
  return next.valid ? next.draft : null;
}
